import React, { useState } from "react";
import { Order } from "./types";
import { getDisplayDate } from "../portal/helpers";
import { Address } from "../portal/types";
import { OrderLineItems } from "./OrderLineItems";
import { Button } from "@/components/Button";
import { ModifiedCharge } from "./RechargeApi";
import {
  CartAddLineItem,
  setCart,
  shopifyCartApi,
  ShopifyCartItem,
} from "@/services/shopifyCart";
import { DroppsProduct, productSelect } from "@/store";
import { storefrontApi } from "@/services/storefront";

type OrderProps = Order & {
  subsDrawerAction: (
    addressid: number,
    upcomingCharge: ModifiedCharge | null
  ) => void;
};

type OrderItemTopProps = {
  createdAt: string;
  orderName: string;
  shippingAddress?: Address;
};

const OrderItemTop = ({
  createdAt,
  orderName,
  shippingAddress,
}: OrderItemTopProps) => {
  const {
    firstName,
    lastName,
    company,
    address1,
    address2,
    city = "",
    province = "",
    zip = "",
  } = shippingAddress || {};

  return (
    <div className="flex w-full flex-wrap md:flex-nowrap">
      <div className="shrink-0 basis-2/4 border-0 border-r border-solid border-primary-200 py-[10px] pr-4 md:basis-auto lg:pr-10">
        <p className="body">Date Placed:</p>
        {createdAt && (
          <h5 className="body text-xl font-semibold text-primary">
            {getDisplayDate(new Date(createdAt), {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </h5>
        )}
      </div>
      <div className="shrink-0 basis-2/4 border-0 px-4 py-[10px] md:basis-auto md:border-r md:border-solid md:border-primary-200 lg:px-10">
        <p className="body">Order Number:</p>
        <h5 className="body text-xl font-semibold text-primary">{orderName}</h5>
      </div>
      <div className="shrink-0 basis-full pt-4 md:grow md:basis-auto md:px-4 md:py-[10px] lg:px-10">
        {shippingAddress && (
          <p className="body text-sm">
            <span className="font-semibold">
              {(firstName || lastName) && `${firstName} ${lastName}`}
              {company && `, ${company}`}
            </span>
            <br />
            {address1}
            {address2 && <span>, {address2}</span>}
            <span className="hidden sm:inline">,&nbsp;</span>
            <br className="sm:hidden" />
            {city}, {province} {zip}
          </p>
        )}
      </div>
    </div>
  );
};

export const OrderItem = ({
  shippingAddress,
  createdAt,
  name: orderName,
  customerUrl,
  lineItems,
  subsDrawerAction,
}: OrderProps) => {
  const [loading, setLoading] = useState(false);
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const shopifyCart = shopifyCartApi.useFetch();
  const allProducts: DroppsProduct[] | undefined =
    storefrontApi.useAllProducts().data?.all;
  const cart = shopifyCart.data;
  const availableProducts = lineItems.filter(
    (lineItem) =>
      lineItem.variantId !== null &&
      lineItem.variant !== null &&
      lineItem?.product?.published_at !== null &&
      lineItem.productId !== 6906068828259 &&
      lineItem.productId !== 7845524963427
  );

  const productMap = new Map<number, DroppsProduct>(
    (allProducts ?? []).map((x) => [x.id, x])
  );

  const itemsWithOriginalShopifyProduct: (ShopifyCartItem & {
    shopifyProduct?: DroppsProduct;
  })[] = (cart?.items ?? []).map((item: ShopifyCartItem) => {
    const product = productMap.get(item.product_id);
    return product ? { ...item, shopifyProduct: product } : item;
  });

  // Check for any bundle in cart (for decomposition logic)
  const containsBundleInCart = itemsWithOriginalShopifyProduct.some(
    (x) => !!x.shopifyProduct?.bundledItems
  );

  // Check for any bundle in availableProducts (from the order being reordered)
  const containsBundleInOrder = availableProducts.some((lineItem) => {
    if (!lineItem.productId) return false;
    const product = productMap.get(lineItem.productId);
    return product?.bundledItems && product.tags.includes("bundle");
  });

  const containsBundle = containsBundleInCart || containsBundleInOrder;

  const unbundledCartItems = containsBundle
    ? itemsWithOriginalShopifyProduct.reduce<CartAddLineItem[]>((acc, item) => {
        if (
          item.shopifyProduct?.bundledItems &&
          item.shopifyProduct.tags.includes("bundle")
        ) {
          item.shopifyProduct.bundledItems.forEach((b) => {
            acc.push({
              id: productSelect.defaultVariant(b).id,
              quantity: item.quantity,
              properties: item.properties,
              selling_plan:
                (item.selling_plan_allocation?.selling_plan &&
                  b.sellingPlans.length > 0 &&
                  productSelect.defaultSellingPlan(b).id) ||
                undefined,
            });
          });
        } else {
          acc.push({
            id: item.id,
            quantity: item.quantity,
            properties: item.properties,
            selling_plan: item.selling_plan_allocation?.selling_plan.id,
          });
        }
        return acc;
      }, [])
    : undefined;

  const handleClick = async () => {
    const items: { id: number; quantity: number; selling_plan?: number }[] = [];
    setLoading(true);

    availableProducts.forEach((lineItem) => {
      const {
        quantity,
        variantId: id,
        sellingPlanName,
        product,
        productId,
      } = lineItem;
      if (!id || !product || !productId) return;

      const shopifyProduct = productMap.get(productId);

      // Check if this is a bundle that needs to be unbundled
      if (
        shopifyProduct?.bundledItems &&
        shopifyProduct.tags.includes("bundle")
      ) {
        // Add bundled items instead of the bundle itself
        shopifyProduct.bundledItems.forEach((bundledProduct) => {
          const isSubItem = Boolean(sellingPlanName);
          const sellingPlan =
            isSubItem &&
            bundledProduct.sellingPlans.length > 0 &&
            productSelect.defaultSellingPlan(bundledProduct);

          const item: { id: number; quantity: number; selling_plan?: number } =
            {
              id: productSelect.defaultVariant(bundledProduct).id,
              quantity,
            };
          if (sellingPlan) {
            item.selling_plan = sellingPlan.id;
          }
          items.push(item);
        });
      } else {
        // Add regular item
        const isSubItem = Boolean(sellingPlanName);
        const sellingPlan =
          isSubItem &&
          product?.selling_plan_groups?.[0]?.selling_plans?.find(
            (plan) => plan.name.toLowerCase() === sellingPlanName?.toLowerCase()
          );

        const item: { id: number; quantity: number; selling_plan?: number } = {
          id,
          quantity,
        };
        if (sellingPlan) {
          item.selling_plan = sellingPlan.id;
        }
        items.push(item);
      }
    });

    const body = {
      items, // This contains all the cart items
      sourceModule: "account-page-reorder-btn",
    };

    // If bundled items are present in cart or order items, they need to be split
    if (unbundledCartItems && unbundledCartItems.length > 0) {
      // First set unbundled items in cart to avoid sourceModule issues
      await setCart(unbundledCartItems);
    }

    // Adding the reorder items
    shopifyCartAdder.mutate(body, {
      onSuccess: () => {
        setLoading(false);
        window.location.href = "/checkout";
      },
      onError: () => {
        setLoading(false);
      },
    });
  };

  return (
    <div className="group max-w-full overflow-hidden rounded-lg bg-white shadow transition-all duration-300">
      <div className="relative h-full space-y-4 p-4">
        <div className="space-y-4">
          <OrderItemTop
            createdAt={createdAt}
            orderName={orderName}
            shippingAddress={shippingAddress}
          />
          <OrderLineItems
            customerUrl={customerUrl}
            orderderLineItems={lineItems}
            orderId={orderName}
            subsDrawerAction={(addressid, upcomingCharge) => {
              subsDrawerAction(addressid, upcomingCharge);
            }}
            shippingAddress={shippingAddress}
          />
          <div className="grid grid-flow-row gap-4 md:auto-cols-fr md:grid-flow-col">
            <div className="flex flex-col space-y-4 md:col-span-2 md:flex-row md:justify-end md:space-x-4 md:space-y-0">
              <Button
                component="a"
                href={customerUrl}
                className="bg-secondary-700 not-disabled:hover:bg-secondary-900"
              >
                FULL ORDER DETAILS
              </Button>
              {availableProducts.length > 0 && (
                <Button
                  onClick={handleClick}
                  loading={loading}
                  data-account-btn="reorder"
                >
                  RE-ORDER NOW
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
